import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vehix/portal/components/ui/tabs';
import { Play, Calendar, Activity, GitBranch } from 'lucide-react';
// Forward ref to PR-B6 component — stays in portal until B6 moves it + shims.
// eslint-disable-next-line max-len
import { WorkflowManagementTab } from '@vehix/portal/features/admin/components/WorkflowManagementTab';
// scheduling barrel was moved by PR-B2; relative `./scheduling` resolves correctly post-move.
import { SchedulingTab, ExecutionHistoryTab } from './scheduling';
// pipelines barrel landed in PR-W2; sibling import resolves from this same shared dir.
import { PipelineOrchestratorTab } from './pipelines';

/**
 * WorkflowManagementWrapper
 *
 * A wrapper component that provides tabs for:
 * 1. Manual Trigger - The existing WorkflowManagementTab for on-demand execution
 * 2. Automatic Scheduling - The scheduling feature for automated workflows
 * 3. Execution History - Unified view of all executions with status
 *
 * This keeps the existing WorkflowManagementTab unchanged while adding
 * the new scheduling and execution history functionality.
 */
interface WorkflowManagementWrapperProps {
  initialSubTab?: string | undefined;
  /**
   * When true, the Pipeline Orchestrator sub-tab is hidden. Used by the
   * admin app where Pipelines is a separate sidebar route.
   */
  hidePipelinesTab?: boolean;
}

// eslint-disable-next-line max-lines-per-function
export const WorkflowManagementWrapper: React.FC<WorkflowManagementWrapperProps> = ({
  initialSubTab,
  hidePipelinesTab = false
}) => {
  const validSubTabs = (
    hidePipelinesTab
      ? (['manual', 'scheduling', 'history'] as const)
      : (['manual', 'scheduling', 'history', 'pipelines'] as const)
  ) as readonly ('manual' | 'scheduling' | 'history' | 'pipelines')[];
  type SubTab = 'manual' | 'scheduling' | 'history' | 'pipelines';

  const [activeTab, setActiveTab] = useState<SubTab>(() =>
    initialSubTab && validSubTabs.includes(initialSubTab as SubTab)
      ? (initialSubTab as SubTab)
      : 'manual'
  );

  // Sync when initialSubTab changes (e.g., navigating back from pipeline detail)
  useEffect(() => {
    if (initialSubTab && validSubTabs.includes(initialSubTab as SubTab)) {
      setActiveTab(initialSubTab as SubTab);
    }
  }, [initialSubTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabTriggerClass =
    'h-full rounded-none border-b-2 border-transparent ' +
    'data-[state=active]:border-primary data-[state=active]:bg-transparent px-1 pb-3';

  return (
    <div className='space-y-6'>
      {/* Main Tab Navigation */}
      <Tabs value={activeTab} onValueChange={(v: string) => setActiveTab(v as SubTab)}>
        <TabsList className='bg-transparent h-12 p-0 space-x-6 border-b w-full justify-start'>
          <TabsTrigger value='manual' className={tabTriggerClass}>
            <Play className='h-4 w-4 mr-2' />
            Manual Trigger
          </TabsTrigger>
          <TabsTrigger value='scheduling' className={tabTriggerClass}>
            <Calendar className='h-4 w-4 mr-2' />
            Automatic Scheduling
          </TabsTrigger>
          <TabsTrigger value='history' className={tabTriggerClass}>
            <Activity className='h-4 w-4 mr-2' />
            Execution History
          </TabsTrigger>
          {!hidePipelinesTab && (
            <TabsTrigger value='pipelines' className={tabTriggerClass}>
              <GitBranch className='h-4 w-4 mr-2' />
              Pipeline Orchestrator
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value='manual' className='mt-6'>
          <WorkflowManagementTab />
        </TabsContent>

        <TabsContent value='scheduling' className='mt-6'>
          <SchedulingTab />
        </TabsContent>

        <TabsContent value='history' className='mt-6'>
          <ExecutionHistoryTab />
        </TabsContent>

        {!hidePipelinesTab && (
          <TabsContent value='pipelines' className='mt-6'>
            <PipelineOrchestratorTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default WorkflowManagementWrapper;
